import { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { useLoyaltyProgram } from "./useLoyaltyProgram";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../types/transactions";
import { LoyaltyEarnPreviewResponse } from "../types/loyalty";

export interface LoyaltyPreviewEarnState {
  preview: LoyaltyEarnPreviewResponse | null;
  /** True while the fetch is in flight AND loyalty is enabled + a txn is
   *  set — i.e. a real value could still arrive. False when the program is
   *  off, no txn is loaded, or the fetch has already resolved. Used by the
   *  callsite to render a skeleton placeholder instead of popping the
   *  earned-points line in after the transaction has already painted. */
  isLoading: boolean;
}

/**
 * Fetches the points a transaction is expected to earn once settled, via
 * GET /transactions/{uuid}/loyalty/preview-earn (backend PR #499). Refetches
 * when the transaction's billable shape mutates (line-item count / prices /
 * tips / discounts / paid amount) so the "N points earned" line tracks
 * edits made from either the checkout or the transaction detail modal.
 *
 * Gated on the loyalty program being enabled at the current location. GET
 * failures degrade to `null` silently — the caller hides the earned-points
 * line so an undeployed endpoint or a permission blip never shows a toast.
 * Returns ``{preview, isLoading}`` so the callsite can distinguish "still
 * fetching" from "resolved to nothing" and render a skeleton while the
 * fetch is in flight.
 */
export function useLoyaltyPreviewEarn(
  transaction: GetTransactionsResponse | TransactionResponse | null,
  /** Dollars of picked-but-not-committed loyalty redemption at checkout.
   *  Passed to the backend as `?staged_redemption_dollars=` so the returned
   *  points reflect the truthful post-redemption basis (same math accrual
   *  will run when Charge commits). Absent / 0 → committed-only view. */
  stagedRedemptionDollars: number = 0,
): LoyaltyPreviewEarnState {
  const { getTransactionLoyaltyPreviewEarn } = useApp();
  const { isEnabled } = useLoyaltyProgram();

  const transactionId = transaction?.transaction?.id ?? null;
  const stagedKey = stagedRedemptionDollars > 0 ? stagedRedemptionDollars : 0;

  // Any of these mutating means the expected earn can change — the string
  // key lets one useEffect dep cover them all without re-firing on
  // reference-only reshuffles from unrelated re-renders.
  const mutationKey = useMemo(() => {
    if (!transaction) return "";
    const services = transaction.line_items?.booked_services ?? [];
    const products = transaction.line_items?.products ?? [];
    const payments = transaction.payments ?? [];
    // A line's discount signature: the backend total plus the applied
    // discount ids, so re-valuing or swapping a stacked discount refetches.
    const discountSig = (line: {
      calc__discount_amount?: number;
      discounts?: { id: string }[];
    }) =>
      `${line.calc__discount_amount ?? ""}/${(line.discounts ?? [])
        .map((d) => d.id)
        .join(",")}`;
    const servicesSig = services
      .map(
        (bs) =>
          `${bs.id}:${bs.quantity ?? ""}:${bs.unit_price ?? ""}:${discountSig(
            bs,
          )}:${bs.tip ?? ""}`,
      )
      .join("|");
    const productsSig = products
      .map(
        (p) =>
          `${p.id}:${p.quantity ?? ""}:${p.unit_price ?? ""}:${discountSig(p)}`,
      )
      .join("|");
    // Payment status flips (voided/refunded) can change what's still
    // billable and thus what earn is expected — cover status + amount.
    const paymentsSig = payments
      .map((p) => `${p.id}:${p.status}:${p.amount ?? ""}`)
      .join("|");
    return `${transactionId}|${servicesSig}|${productsSig}|${paymentsSig}|staged:${stagedKey}`;
  }, [transaction, transactionId, stagedKey]);

  const willFetch = isEnabled && !!transactionId;
  const [state, setState] = useState<LoyaltyPreviewEarnState>({
    preview: null,
    isLoading: willFetch,
  });

  useEffect(() => {
    if (!isEnabled || !transactionId) {
      setState({ preview: null, isLoading: false });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ preview: prev.preview, isLoading: true }));
    (async () => {
      try {
        const response = await getTransactionLoyaltyPreviewEarn(
          transactionId,
          stagedKey,
        );
        if (!cancelled)
          setState({ preview: response ?? null, isLoading: false });
      } catch {
        // Endpoint may not be deployed yet, or the client is not opted in —
        // hide silently; no toast.
        if (!cancelled) setState({ preview: null, isLoading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isEnabled,
    transactionId,
    mutationKey,
    stagedKey,
    getTransactionLoyaltyPreviewEarn,
  ]);

  return state;
}

export default useLoyaltyPreviewEarn;
